import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, History } from "lucide-react";
import { api, withReason } from "@/lib/api";
import {
  blankCondition,
  blankRule,
  byRuleIndex,
  fromRuleset,
  isPayloadField,
  operatorsFor,
  relaxesAFlooredAction,
  summarise,
  toPayload,
  valueTypeFor,
  PAYLOAD_PREFIX,
} from "@/lib/policy";

/**
 * Policy rules for one workspace — shared by the staff console and the
 * workspace's own Settings page.
 *
 * The page owns the fetch and passes the result in; this owns edit state only.
 * Rules nest one level above conditions, so the updaters are two-deep — the
 * same index-keyed immutable pattern as the onboarding form, which exists
 * because mutating a row in place makes removing one leave the wrong content
 * behind.
 *
 * `basePath` picks which door a save goes through: the staff console reaches
 * a named tenant's rules (`/platform/tenants/{id}/policy`, `X-Platform-Reason`
 * required — there's someone else to explain the change to); self-serve
 * reaches the caller's own (`/policy`, no reason required — a workspace owner
 * changing their own policy has no one else to justify it to).
 */
export default function PolicyRulesEditor({
  tenantId, policy, catalogue, onSaved,
  basePath = `/platform/tenants/${tenantId}/policy`,
  requireReason = true,
}) {
  const [rules, setRules] = useState(() => fromRuleset(policy?.ruleset));
  const [open, setOpen] = useState({});
  const [errors, setErrors] = useState({});
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [revisions, setRevisions] = useState(null);

  const revision = policy?.revision ?? 0;
  const enabled = policy?.enabled !== false;

  const setRule = (i, k, v) =>
    setRules((rs) => rs.map((r, x) => (x === i ? { ...r, [k]: v } : r)));

  const setCond = (i, j, k, v) =>
    setRules((rs) =>
      rs.map((r, x) =>
        x !== i
          ? r
          : { ...r, when: r.when.map((c, y) => (y === j ? { ...c, [k]: v } : c)) }
      )
    );

  // Changing a field can strand an operator the new field does not offer, and
  // the server would reject it with a message about a rule the author thought
  // they had just fixed. Reset to the first valid one instead.
  const setCondField = (i, j, field) =>
    setRules((rs) =>
      rs.map((r, x) =>
        x !== i
          ? r
          : {
              ...r,
              when: r.when.map((c, y) => {
                if (y !== j) return c;
                const next = { ...c, field, value: "" };
                const allowed = operatorsFor(catalogue, next);
                if (!allowed.includes(next.op)) next.op = allowed[0] || "eq";
                return next;
              }),
            }
      )
    );

  const addCond = (i) =>
    setRules((rs) =>
      rs.map((r, x) => (x === i ? { ...r, when: [...r.when, blankCondition()] } : r))
    );

  const dropCond = (i, j) =>
    setRules((rs) =>
      rs.map((r, x) =>
        x !== i ? r : { ...r, when: r.when.filter((_, y) => y !== j) }
      )
    );

  const addRule = () => {
    setRules((rs) => [...rs, blankRule()]);
    setOpen((o) => ({ ...o, [rules.length]: true }));
  };

  const dropRule = (i) => {
    if (!window.confirm("Remove this rule? It is not gone until you save.")) return;
    setRules((rs) => rs.filter((_, x) => x !== i));
  };

  const loadHistory = async () => {
    if (revisions) return setRevisions(null);
    try {
      const { data } = await api.get(`${basePath}/revisions`);
      setRevisions(data.revisions || []);
    } catch {
      setRevisions([]);
    }
  };

  const save = async () => {
    let reason = "";
    if (requireReason) {
      reason = window.prompt("Why are you changing this workspace's policy?");
      if (!reason || !reason.trim()) return;
    }
    if (
      !window.confirm(
        `Save revision ${revision + 1}? It takes effect on this workspace's next request.`
      )
    )
      return;

    setBusy(true);
    setErrors({});
    setConflict(false);
    try {
      await api.post(
        `${basePath}/revisions`,
        { rules: toPayload(rules, catalogue), expected_revision: revision },
        requireReason ? withReason(reason.trim()) : {}
      );
      setMessage("Saved.");
      if (onSaved) onSaved();
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflict(true);
      } else {
        const details = err?.response?.data?.details?.errors;
        if (details) setErrors(byRuleIndex(details));
        else setMessage(err?.response?.data?.message || "Could not save these rules.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="policy-editor">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-display text-[18px]">Approval rules</h2>
        <div className="flex items-center gap-2">
          <span className="text-[11.5px] font-mono" style={{ color: "var(--text-muted)" }}>
            {policy?.version || "policy-v1"}
          </span>
          <button className="btn btn-ghost" onClick={loadHistory} data-testid="policy-history-toggle">
            <History size={12} /> History
          </button>
          <button className="btn" onClick={addRule} data-testid="add-policy-rule">
            <Plus size={12} /> Add rule
          </button>
        </div>
      </div>

      {!enabled && (
        <div className="badge b-sla mb-3" data-testid="policy-disabled-banner">
          Stored, but not in force: the {policy?.plan_code || "current"} plan does not
          include policy rules.
        </div>
      )}

      {policy?.valid === false && (
        <div className="card p-3 mb-3" data-testid="policy-invalid-banner">
          <div className="badge b-rev mb-2">
            These stored rules are not valid and are being ignored — this workspace is
            running on built-in policy only.
          </div>
          <ul className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
            {(policy.errors || []).map((e, i) => (
              <li key={i}>
                rule {e.index + 1}
                {e.field ? ` · ${e.field}` : ""}: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {conflict && (
        <div className="badge b-rev mb-3" data-testid="policy-conflict-banner">
          Someone else changed this workspace's policy while you were editing. Reload
          before saving.
        </div>
      )}

      {message && (
        <div className="badge b-muted mb-3" data-testid="policy-message">{message}</div>
      )}

      <p className="text-[12px] mb-3" style={{ color: "var(--text-muted)" }}>
        Rules may make this workspace stricter than the built-in policy. Actions that
        move money or reach a customer always require a human, whatever a rule says.
      </p>

      {rules.length === 0 && (
        <div className="card p-8 text-center text-[13px]" style={{ color: "var(--text-muted)" }}
             data-testid="policy-empty">
          No rules. This workspace uses the built-in policy alone.
        </div>
      )}

      {rules.map((rule, i) => (
        <div key={i} className="card p-3 mb-2" data-testid={`policy-rule-row-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <button
              className="btn btn-ghost text-left shrink-0 whitespace-nowrap"
              onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
              data-testid={`toggle-policy-rule-${i}`}
            >
              {open[i] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span className="font-mono text-[12px]">{rule.id || "(unnamed rule)"}</span>
            </button>
            <span
              className="text-[12px] flex-1 min-w-0 truncate"
              title={summarise(rule, catalogue)}
              style={{ color: "var(--text-secondary)" }}
            >
              {summarise(rule, catalogue)}
            </span>
            <button className="btn btn-ghost btn-danger" onClick={() => dropRule(i)}
                    data-testid={`remove-policy-rule-${i}`}>
              <Trash2 size={12} />
            </button>
          </div>

          {relaxesAFlooredAction(rule, catalogue) && (
            <div className="badge b-sla mt-2" data-testid={`policy-floored-warning-${i}`}>
              This relaxes an action that moves money or reaches a customer. It will be
              stored, but the built-in protection still applies.
            </div>
          )}

          {(errors[i] || []).map((e, x) => (
            <div key={x} className="badge b-rev mt-2" data-testid={`policy-rule-error-${i}`}>
              {e.message}
            </div>
          ))}

          {open[i] && (
            <div className="mt-3">
              <div className="grid grid-cols-12 gap-2 mb-2">
                <input
                  className="input col-span-12 md:col-span-3"
                  placeholder="rule id"
                  value={rule.id}
                  onChange={(e) => setRule(i, "id", e.target.value)}
                  data-testid={`policy-rule-id-${i}`}
                />
                <select
                  className="input col-span-12 md:col-span-3"
                  value={rule.effect}
                  onChange={(e) => setRule(i, "effect", e.target.value)}
                  data-testid={`policy-rule-effect-${i}`}
                >
                  {(catalogue?.effects || []).map((eff) => (
                    <option key={eff} value={eff}>{eff}</option>
                  ))}
                </select>
                <input
                  className="input col-span-12 md:col-span-6"
                  placeholder="why this rule exists"
                  value={rule.description}
                  onChange={(e) => setRule(i, "description", e.target.value)}
                  data-testid={`policy-rule-description-${i}`}
                />
              </div>

              {rule.effect === "REQUIRE_APPROVAL" && (
                <div className="mb-2 text-[12px]" style={{ color: "var(--text-muted)" }}>
                  Approvers:{" "}
                  {(catalogue?.roles || []).map((role) => (
                    <label key={role} className="mr-3">
                      <input
                        type="checkbox"
                        checked={(rule.approval_roles || []).includes(role)}
                        onChange={(e) =>
                          setRule(
                            i,
                            "approval_roles",
                            e.target.checked
                              ? [...(rule.approval_roles || []), role]
                              : (rule.approval_roles || []).filter((r) => r !== role)
                          )
                        }
                        data-testid={`policy-rule-role-${i}-${role}`}
                      />{" "}
                      {role}
                    </label>
                  ))}
                </div>
              )}

              {(rule.when || []).map((condition, j) => {
                const type = valueTypeFor(catalogue, condition);
                const descriptor = (catalogue?.fields || []).find(
                  (f) => f.field === condition.field
                );
                return (
                  <div key={j} className="grid grid-cols-12 gap-2 mb-2"
                       data-testid={`policy-cond-row-${i}-${j}`}>
                    <select
                      className={`input ${isPayloadField(condition.field) ? "col-span-12 md:col-span-3" : "col-span-12 md:col-span-5"}`}
                      value={isPayloadField(condition.field) ? PAYLOAD_PREFIX : condition.field}
                      onChange={(e) => setCondField(i, j, e.target.value)}
                      data-testid={`policy-cond-field-${i}-${j}`}
                    >
                      {(catalogue?.fields || []).map((f) => (
                        <option key={f.field} value={f.field}>{f.label}</option>
                      ))}
                      <option value={PAYLOAD_PREFIX}>Action detail (payload…)</option>
                    </select>

                    {isPayloadField(condition.field) && (
                      <input
                        className="input col-span-6 md:col-span-2"
                        placeholder="amount"
                        value={condition.field.slice(PAYLOAD_PREFIX.length)}
                        onChange={(e) =>
                          setCond(i, j, "field", PAYLOAD_PREFIX + e.target.value)
                        }
                        data-testid={`policy-cond-payload-name-${i}-${j}`}
                      />
                    )}

                    <select
                      className="input col-span-6 md:col-span-2"
                      value={condition.op}
                      onChange={(e) => setCond(i, j, "op", e.target.value)}
                      data-testid={`policy-cond-op-${i}-${j}`}
                    >
                      {operatorsFor(catalogue, condition).map((op) => (
                        <option key={op} value={op}>{op}</option>
                      ))}
                    </select>

                    {type === "bool" || condition.op === "exists" ? (
                      <select
                        className="input col-span-6 md:col-span-2"
                        value={String(condition.value)}
                        onChange={(e) => setCond(i, j, "value", e.target.value)}
                        data-testid={`policy-cond-value-${i}-${j}`}
                      >
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    ) : descriptor?.options && condition.op !== "in" && condition.op !== "not_in" ? (
                      <select
                        className="input col-span-12 md:col-span-3"
                        value={condition.value}
                        onChange={(e) => setCond(i, j, "value", e.target.value)}
                        data-testid={`policy-cond-value-${i}-${j}`}
                      >
                        <option value="">choose…</option>
                        {descriptor.options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="input col-span-12 md:col-span-3"
                        type={type === "number" ? "number" : "text"}
                        placeholder={condition.op === "in" ? "a, b, c" : "value"}
                        value={condition.value}
                        onChange={(e) => setCond(i, j, "value", e.target.value)}
                        data-testid={`policy-cond-value-${i}-${j}`}
                      />
                    )}

                    {isPayloadField(condition.field) && condition.op !== "exists" && (
                      <select
                        className="input col-span-6 md:col-span-1"
                        value={condition.valueType || "text"}
                        onChange={(e) => setCond(i, j, "valueType", e.target.value)}
                        data-testid={`policy-cond-valuetype-${i}-${j}`}
                      >
                        <option value="text">text</option>
                        <option value="number">number</option>
                        <option value="bool">bool</option>
                      </select>
                    )}

                    <button
                      className="btn btn-ghost col-span-6 md:col-span-1"
                      onClick={() => dropCond(i, j)}
                      data-testid={`remove-policy-cond-${i}-${j}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                );
              })}

              <button className="btn btn-ghost" onClick={() => addCond(i)}
                      data-testid={`add-policy-cond-${i}`}>
                <Plus size={11} /> Add condition
              </button>
            </div>
          )}
        </div>
      ))}

      <button className="btn mt-3" onClick={save} disabled={busy} data-testid="save-policy">
        {busy ? "Saving…" : `Save revision ${revision + 1}`}
      </button>

      {revisions && (
        <div className="card overflow-x-auto mt-4" data-testid="policy-revisions">
          <table className="w-full min-w-[640px] text-[13px]">
            <thead>
              <tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Rev</th>
                <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">When</th>
                <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Who</th>
                <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Rules</th>
                <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Reason</th>
              </tr>
            </thead>
            <tbody>
              {revisions.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center" style={{ color: "var(--text-muted)" }}>
                  No revisions yet.
                </td></tr>
              )}
              {revisions.map((r) => (
                <tr key={r.revision} style={{ borderBottom: "1px solid var(--border)" }}
                    data-testid={`policy-revision-${r.revision}`}>
                  <td className="py-2.5 px-3 font-mono">
                    {r.revision}{r.is_current ? " ·" : ""}
                  </td>
                  <td className="py-2.5 px-3 font-mono text-[11.5px]">{r.created_at}</td>
                  <td className="py-2.5 px-3">{r.created_by || "—"}</td>
                  <td className="py-2.5 px-3 font-mono">{r.rule_count}</td>
                  <td className="py-2.5 px-3" style={{ color: "var(--text-secondary)" }}>
                    {r.reason || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
